import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, InputNumber, notification, Button, List, Flex } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
  createProductExtensionCategory,
  editProductExtensionCategory,
  removeProductExtensionCategory,
  createProductExtensionVariant,
  deleteProductExtensionVariant,
  renameProductExtensionVariant,
  getExtensions
} from "../../../service";
import { colors, spacing } from "../../../styles/theme";
import { useAdditionsTour } from "../../../contexts/additions-tour-context/useAdditionsTour";

interface AddExtensionCategoryModalProps {
  open: boolean;
  onCancel: () => void;
  category?: any; // Jeśli jest category, to tryb edycji
  onCreated?: (categoryId: number) => void; // Callback po utworzeniu kategorii
}

const EXTENSION_TYPES = [
  {
    value: 'SELECT',
    labelKey: 'addExtensionCategory.typeSelectLabel',
    descriptionKey: 'addExtensionCategory.typeSelectDescription',
  },
  {
    value: 'MULTISELECT',
    labelKey: 'addExtensionCategory.typeMultiselectLabel',
    descriptionKey: 'addExtensionCategory.typeMultiselectDescription',
  },
  {
    value: 'QUANTITIES',
    labelKey: 'addExtensionCategory.typeQuantitiesLabel',
    descriptionKey: 'addExtensionCategory.typeQuantitiesDescription',
  },
];

export const AddExtensionCategoryModal: React.FC<AddExtensionCategoryModalProps> = ({
  open,
  onCancel,
  category,
  onCreated,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const isEditMode = !!category;
  const [variants, setVariants] = useState<string[]>([]);
  const [newVariantName, setNewVariantName] = useState("");
  const [editingVariant, setEditingVariant] = useState<string | null>(null);
  const [editingVariantValue, setEditingVariantValue] = useState("");
  const { isTourOpen, activeTour } = useAdditionsTour();
  // During the "create-additions" tour we lock the modal so user must complete it
  const isTourLocked = isTourOpen && activeTour === "create-additions" && !isEditMode;

  // Reset formularza gdy modal się otwiera
  useEffect(() => {
    if (open && category) {
      const isRequired = category.type === 'SELECT' && category.minimumRequiredElements >= 1;
      form.setFieldsValue({
        name: category.name,
        type: category.type,
        minimumRequiredElements: category.minimumRequiredElements || 0,
        maximumRequiredElements: category.maximumRequiredElements || 0,
        isRequired: isRequired,
      });

      // Pobierz warianty cenowe dla tej kategorii
      fetchVariants();
    } else if (open && !category) {
      form.resetFields();
      setVariants([]);
    }
  }, [open, category, form]);

  const fetchVariants = async () => {
    if (!category?.id) return;

    try {
      const data = await getExtensions({ categoryId: category.id });
      // Zbierz unikalne nazwy wariantów ze wszystkich dodatków w kategorii
      const uniqueVariants = new Set<string>();
      if (data?.extensions && Array.isArray(data.extensions)) {
        data.extensions.forEach((ext: any) => {
          ext.productExtensionVariant?.forEach((variant: any) => {
            uniqueVariants.add(variant.name);
          });
        });
      }
      setVariants(Array.from(uniqueVariants));
    } catch (error) {
      console.error("Error fetching variants:", error);
    }
  };

  const { mutateAsync: createMutation, isLoading: isCreating } = useMutation(
    createProductExtensionCategory,
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(["/product-extension-categories"]);
        notification.success({
          message: t("addExtensionCategory.categoryCreatedSuccess"),
          style: { background: colors.successBackground },
          placement: "bottomRight",
        });
        form.resetFields();
        onCancel();
        // Wywołaj callback z ID nowej kategorii
        if (data?.id && onCreated) {
          onCreated(data.id);
        }
      },
      onError: (error: any) => {
        notification.error({
          message: t("addExtensionCategory.categoryCreateError"),
          description: error?.response?.data?.message || t("addExtensionCategory.tryAgain"),
          style: { background: colors.errorBackground },
          placement: "bottomRight",
        });
      },
    }
  );

  const { mutateAsync: updateMutation, isLoading: isUpdating } = useMutation(
    editProductExtensionCategory,
    {
      onMutate: async (variables) => {
        // Cancel ongoing queries
        await queryClient.cancelQueries(["/product-extension-categories"]);

        // Snapshot the previous value
        const previousData = queryClient.getQueryData(["/product-extension-categories"]);

        // Optimistically update to the new value
        queryClient.setQueryData(["/product-extension-categories"], (old: any) => {
          if (!Array.isArray(old)) return old;
          return old.map((cat: any) =>
            cat.id === variables.id
              ? { ...cat, ...variables.patchData }
              : cat
          );
        });

        return { previousData };
      },
      onSuccess: () => {
        // NIE invalidujemy query, aby zachować kolejność
        // Optimistic update już zaktualizował stan w cache
        notification.success({
          message: t("addExtensionCategory.categoryUpdatedSuccess"),
          style: { background: colors.successBackground },
          placement: "bottomRight",
        });
        onCancel();
      },
      onError: (error: any, variables, context: any) => {
        // Rollback on error
        if (context?.previousData) {
          queryClient.setQueryData(["/product-extension-categories"], context.previousData);
        }
        notification.error({
          message: t("addExtensionCategory.categoryUpdateError"),
          description: error?.response?.data?.message || t("addExtensionCategory.tryAgain"),
          style: { background: colors.errorBackground },
          placement: "bottomRight",
        });
      },
    }
  );

  const { mutateAsync: deleteMutation, isLoading: isDeleting } = useMutation(
    removeProductExtensionCategory,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["/product-extension-categories"]);
        notification.success({
          message: t("addExtensionCategory.categoryDeletedSuccess"),
          style: { background: colors.successBackground },
          placement: "bottomRight",
        });
        onCancel();
      },
      onError: (error: any) => {
        notification.error({
          message: t("addExtensionCategory.categoryDeleteError"),
          description: error?.response?.data?.message || t("addExtensionCategory.categoryDeleteErrorDescription"),
          style: { background: colors.errorBackground },
          placement: "bottomRight",
        });
      },
    }
  );

  const { mutateAsync: addVariantMutation } = useMutation(
    createProductExtensionVariant,
    {
      onSuccess: () => {
        fetchVariants();
        setNewVariantName("");
        notification.success({
          message: t("addExtensionCategory.variantAddedSuccess"),
          style: { background: colors.successBackground },
          placement: "bottomRight",
        });
        queryClient.invalidateQueries([`/additions-category-${category.id}`]);
      },
      onError: (error: any) => {
        notification.error({
          message: t("addExtensionCategory.variantAddError"),
          description: error?.response?.data?.message || t("addExtensionCategory.tryAgain"),
          style: { background: colors.errorBackground },
          placement: "bottomRight",
        });
      },
    }
  );

  const { mutateAsync: removeVariantMutation } = useMutation(
    deleteProductExtensionVariant,
    {
      onSuccess: () => {
        fetchVariants();
        notification.success({
          message: t("addExtensionCategory.variantRemovedSuccess"),
          style: { background: colors.successBackground },
          placement: "bottomRight",
        });
        queryClient.invalidateQueries([`/additions-category-${category.id}`]);
      },
      onError: (error: any) => {
        notification.error({
          message: t("addExtensionCategory.variantRemoveError"),
          description: error?.response?.data?.message || t("addExtensionCategory.tryAgain"),
          style: { background: colors.errorBackground },
          placement: "bottomRight",
        });
      },
    }
  );

  const { mutateAsync: renameVariantMutation } = useMutation(
    renameProductExtensionVariant,
    {
      onSuccess: () => {
        fetchVariants();
        setEditingVariant(null);
        setEditingVariantValue("");
        notification.success({
          message: t("addExtensionCategory.variantRenamedSuccess"),
          style: { background: colors.successBackground },
          placement: "bottomRight",
        });
        queryClient.invalidateQueries([`/additions-category-${category.id}`]);
      },
      onError: (error: any) => {
        notification.error({
          message: t("addExtensionCategory.variantRenameError"),
          description: error?.response?.data?.message || t("addExtensionCategory.tryAgain"),
          style: { background: colors.errorBackground },
          placement: "bottomRight",
        });
      },
    }
  );

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      let minimumRequiredElements = values.minimumRequiredElements || 0;
      let maximumRequiredElements = values.maximumRequiredElements || 0;

      // Dla SELECT, mapuj isRequired na min/max
      if (values.type === 'SELECT') {
        if (values.isRequired) {
          minimumRequiredElements = 1;
          maximumRequiredElements = 1;
        } else {
          minimumRequiredElements = 0;
          maximumRequiredElements = 1;
        }
      }

      const data = {
        name: values.name,
        type: values.type,
        minimumRequiredElements,
        maximumRequiredElements,
      };

      if (isEditMode) {
        await updateMutation({
          id: category.id,
          patchData: data,
        });
      } else {
        await createMutation(data);
      }
    } catch (error) {
      console.error("Form validation error:", error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setEditingVariant(null);
    setEditingVariantValue("");
    onCancel();
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t("addExtensionCategory.deleteConfirmTitle"),
      content: t("addExtensionCategory.deleteConfirmContent"),
      okText: t("addExtensionCategory.delete"),
      okType: "danger",
      cancelText: t("addExtensionCategory.cancel"),
      onOk: async () => {
        await deleteMutation({ id: category.id });
      },
    });
  };

  const handleAddVariant = async () => {
    if (!newVariantName.trim()) {
      notification.warning({
        message: t("addExtensionCategory.enterVariantName"),
        placement: "bottomRight",
      });
      return;
    }

    if (variants.includes(newVariantName.trim())) {
      notification.warning({
        message: t("addExtensionCategory.variantNameExists"),
        placement: "bottomRight",
      });
      return;
    }

    await addVariantMutation({
      extensionCategoryId: category.id,
      name: newVariantName.trim(),
      priceExtension: 0,
    });
  };

  const handleRemoveVariant = (variantName: string) => {
    Modal.confirm({
      title: t("addExtensionCategory.removeVariantConfirmTitle"),
      content: t("addExtensionCategory.removeVariantConfirmContent", { variantName }),
      okText: t("addExtensionCategory.delete"),
      okType: "danger",
      cancelText: t("addExtensionCategory.cancel"),
      onOk: async () => {
        await removeVariantMutation({ variantName });
      },
    });
  };

  const handleStartEditVariant = (variantName: string) => {
    setEditingVariant(variantName);
    setEditingVariantValue(variantName);
  };

  const handleCancelEditVariant = () => {
    setEditingVariant(null);
    setEditingVariantValue("");
  };

  const handleSaveVariantRename = async (oldName: string) => {
    const newName = editingVariantValue.trim();
    if (!newName) {
      notification.warning({ message: t("addExtensionCategory.enterVariantName"), placement: "bottomRight" });
      return;
    }
    if (newName === oldName) {
      handleCancelEditVariant();
      return;
    }
    if (variants.includes(newName)) {
      notification.warning({ message: t("addExtensionCategory.variantNameExists"), placement: "bottomRight" });
      return;
    }
    await renameVariantMutation({ oldName, newName, categoryId: category.id });
  };

  const selectedType = Form.useWatch('type', form);

  useEffect(() => {
    if (!isEditMode && selectedType === 'MULTISELECT') {
      form.setFieldValue('maximumRequiredElements', 10);
    }
  }, [selectedType, isEditMode]);

  return (
    <Modal
      title={isEditMode ? t("addExtensionCategory.editModalTitle") : t("addExtensionCategory.addModalTitle")}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={isEditMode ? t("addExtensionCategory.saveChanges") : t("addExtensionCategory.createCategory")}
      cancelText={t("addExtensionCategory.cancel")}
      confirmLoading={isCreating || isUpdating || isDeleting}
      width={600}
      closable={!isTourLocked}
      maskClosable={!isTourLocked}
      keyboard={!isTourLocked}
      footer={
        isEditMode
          ? [
            <Button key="delete" danger onClick={handleDelete} loading={isDeleting}>
              {t("addExtensionCategory.deleteCategory")}
            </Button>,
            <Button key="cancel" onClick={handleCancel}>
              {t("addExtensionCategory.cancel")}
            </Button>,
            <Button key="submit" type="primary" onClick={handleSubmit} loading={isUpdating}>
              {t("addExtensionCategory.saveChanges")}
            </Button>,
          ]
          : isTourLocked
            ? (
              // Tour mode: only show the submit button, no cancel or close
              <Flex justify="flex-end">
                <Button type="primary" onClick={handleSubmit} loading={isCreating}>
                  {t("addExtensionCategory.createCategory")}
                </Button>
              </Flex>
            )
            : undefined
      }
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        initialValues={{
          minimumRequiredElements: 0,
          maximumRequiredElements: 0,
          isRequired: false,
        }}
      >
        <Form.Item
          name="name"
          label={t("addExtensionCategory.nameLabel")}
          rules={[{ required: true, message: t("addExtensionCategory.nameRequired") }]}
        >
          <Input placeholder={t("addExtensionCategory.namePlaceholder")} autoFocus />
        </Form.Item>

        <Form.Item
          name="type"
          label={t("addExtensionCategory.typeLabel")}
          rules={[{ required: true, message: t("addExtensionCategory.typeRequired") }]}
          tooltip={t("addExtensionCategory.typeTooltip")}
        >
          <Select
            placeholder={t("addExtensionCategory.typePlaceholder")}
            optionLabelProp="label"
            options={EXTENSION_TYPES.map(type => ({
              value: type.value,
              label: t(type.labelKey),
              description: t(type.descriptionKey),
            }))}
            optionRender={(option) => (
              <div>
                <div style={{ fontWeight: 500 }}>{option.label}</div>
                <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                  {option.data.description}
                </div>
              </div>
            )}
          />
        </Form.Item>

        {selectedType === 'SELECT' && (
          <Form.Item
            name="isRequired"
            label={t("addExtensionCategory.isRequiredLabel")}
            tooltip={t("addExtensionCategory.isRequiredTooltip")}
          >
            <Select
              placeholder={t("addExtensionCategory.isRequiredPlaceholder")}
              options={[
                { value: true, label: t("addExtensionCategory.isRequiredYes") },
                { value: false, label: t("addExtensionCategory.isRequiredNo") },
              ]}
              onChange={(value) => {
                // Jeśli obowiązkowy, ustaw minimum na 1
                if (value) {
                  form.setFieldValue('minimumRequiredElements', 1);
                  form.setFieldValue('maximumRequiredElements', 1);
                } else {
                  form.setFieldValue('minimumRequiredElements', 0);
                  form.setFieldValue('maximumRequiredElements', 1);
                }
              }}
            />
          </Form.Item>
        )}

        {selectedType === 'MULTISELECT' && (
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="minimumRequiredElements"
              label={t("addExtensionCategory.minimumLabel")}
              tooltip={t("addExtensionCategory.minimumTooltip")}
              style={{ flex: 1 }}
              rules={[
                { required: true, message: t("addExtensionCategory.minimumRequired") },
                {
                  validator: (_, value) => {
                    const max = form.getFieldValue('maximumRequiredElements');
                    if (max && value > max) {
                      return Promise.reject(t("addExtensionCategory.minimumGreaterThanMaximum"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="0"
              />
            </Form.Item>

            <Form.Item
              name="maximumRequiredElements"
              label={t("addExtensionCategory.maximumLabel")}
              tooltip={t("addExtensionCategory.maximumTooltip")}
              style={{ flex: 1 }}
              rules={[
                { required: true, message: t("addExtensionCategory.maximumRequired") },
                {
                  validator: (_, value) => {
                    const min = form.getFieldValue('minimumRequiredElements');
                    if (value > 0 && min > value) {
                      return Promise.reject(t("addExtensionCategory.maximumLessThanMinimum"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder={t("addExtensionCategory.maximumPlaceholder")}
              />
            </Form.Item>
          </div>
        )}

        {isEditMode && (
          <>
            <div style={{
              borderTop: `1px solid ${colors.borderLight}`,
              marginTop: spacing.lg,
              paddingTop: spacing.lg
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: spacing.sm
              }}>
                {t("addExtensionCategory.variantsSectionTitle")}
              </div>
              <div style={{
                fontSize: '12px',
                color: colors.textSecondary,
                marginBottom: spacing.md
              }}>
                {t("addExtensionCategory.variantsSectionDescription")}
              </div>

              {/* Lista istniejących wariantów */}
              {variants.length > 0 && (
                <List
                  size="small"
                  bordered
                  dataSource={variants}
                  style={{ marginBottom: spacing.md }}
                  renderItem={(variantName) => {
                    const isEditing = editingVariant === variantName;
                    return (
                      <List.Item
                        actions={
                          isEditing
                            ? [
                                <Button
                                  key="save"
                                  type="text"
                                  size="small"
                                  icon={<CheckOutlined />}
                                  onClick={() => handleSaveVariantRename(variantName)}
                                />,
                                <Button
                                  key="cancel"
                                  type="text"
                                  size="small"
                                  icon={<CloseOutlined />}
                                  onClick={handleCancelEditVariant}
                                />,
                              ]
                            : [
                                <Button
                                  key="edit"
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => handleStartEditVariant(variantName)}
                                />,
                                <Button
                                  key="delete"
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => handleRemoveVariant(variantName)}
                                />,
                              ]
                        }
                      >
                        {isEditing ? (
                          <Input
                            size="small"
                            value={editingVariantValue}
                            onChange={(e) => setEditingVariantValue(e.target.value)}
                            onPressEnter={() => handleSaveVariantRename(variantName)}
                            autoFocus
                            style={{ width: '100%', maxWidth: 260 }}
                          />
                        ) : (
                          variantName
                        )}
                      </List.Item>
                    );
                  }}
                />
              )}

              {/* Formularz dodawania nowego wariantu */}
              <Flex gap={spacing.sm}>
                <Input
                  placeholder={t("addExtensionCategory.newVariantPlaceholder")}
                  value={newVariantName}
                  onChange={(e) => setNewVariantName(e.target.value)}
                  onPressEnter={handleAddVariant}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddVariant}
                >
                  {t("addExtensionCategory.addVariant")}
                </Button>
              </Flex>
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};
